import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import {
  getCategoryControls,
  isDataControl,
  isValidControl,
  pluralize,
  queries,
  useAuthConfig,
  ValidControlCategory,
} from "@theprelude/core";
import {
  Button,
  ChevronIcon,
  ControlIcon,
  PlatformIcon,
  SettingsErrorIcon,
  Truncate,
} from "@theprelude/ds";
import { z } from "zod";
import { DefaultOverlay } from "@/components/default-overlay";
import { SummaryIconBubble } from "@/components/executive-summary/summary-icon-bubbles";
import { ResponsiveContainer } from "@/components/responsive-container";
import { useTable } from "@/components/table/hooks";
import { VirtualTable } from "@/components/table/virtual-table";
import { validControlCategorySchema } from "@/lib/validators";
import { CriticalSetting, criticalSettingsQuery } from "@/queries/policies";

export const criticalPoliciesOverlaySchema = z.tuple([
  z.literal("critical-policies"),
  z.object({
    category: validControlCategorySchema,
  }),
]);

export type CriticalPoliciesSchema = z.infer<
  typeof criticalPoliciesOverlaySchema
>[1];

export const CriticalPoliciesOverlay = ({
  category,
}: CriticalPoliciesSchema) => (
  <DefaultOverlay
    title="Critical Policy Setting Misconfigurations"
    defaultSize={60}
  >
    <div className="p-4">
      <ResponsiveContainer>
        {({ height }) => (
          <CriticalPoliciesTable category={category} height={height} />
        )}
      </ResponsiveContainer>
    </div>
  </DefaultOverlay>
);

const columnHelper = createColumnHelper<CriticalSetting>();
const columns = [
  columnHelper.accessor("name", {
    enableSorting: true,
    enableHiding: false,
    cell: (info) => (
      <div className="flex items-center gap-1">
        <SummaryIconBubble status="negative" size="small">
          <SettingsErrorIcon />
        </SummaryIconBubble>
        <div className="min-w-0 text-status-red font-medium">
          <Truncate text={info.getValue()}>{info.getValue()}</Truncate>
        </div>
      </div>
    ),
    meta: {
      label: "Name",
      widths: ["200px", "3fr"],
    },
  }),
  columnHelper.accessor("policyName", {
    enableHiding: false,
    enableSorting: true,
    cell: (info) => (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 pl-1">
          <ControlIcon
            control={info.row.original.control}
            className="text-secondary-foreground"
          />
          {info.row.original.platform && (
            <ChevronIcon className="rotate-90 text-secondary-foreground" />
          )}
          {info.row.original.platform && (
            <PlatformIcon
              platform={info.row.original.platform}
              className="text-secondary-foreground"
            />
          )}
        </div>
        <Truncate text={info.getValue()}>{info.getValue()}</Truncate>
      </div>
    ),
    meta: {
      label: "Policy",
      widths: ["200px", "2fr"],
      canExport: false,
    },
  }),
  columnHelper.accessor("resourceCount", {
    enableHiding: false,
    enableSorting: true,
    cell: (info) => {
      const count = info.getValue();
      const type = info.row.original.resourceType;
      const text = [
        count.toLocaleString(),
        pluralize(count, type, type === "inbox" ? "inboxes" : undefined),
      ].join(" ");

      return <Truncate text={text}>{text}</Truncate>;
    },
    meta: {
      label: "Affected",
      widths: ["100px", "1fr"],
      canExport: false,
    },
  }),
  columnHelper.display({
    id: "actions",
    enableHiding: false,
    enableSorting: false,
    cell: (info) => (
      <Link
        to={window.location.pathname}
        params={(prev) => prev}
        search={(prev) => ({
          ...prev,
          overlay: [
            "policies",
            {
              filter: "critical",
              control: info.row.original.control,
              instanceId: info.row.original.instanceId,
            },
          ],
        })}
      >
        <Button intent="pill">View Policy</Button>
      </Link>
    ),
    meta: {
      label: "Issues",
      widths: ["80px", "1fr"],
      canExport: false,
    },
  }),
];

const CriticalPoliciesTable = ({
  category,
  height,
}: {
  category: ValidControlCategory;
  height: number;
}) => {
  const authConfig = useAuthConfig();
  const controls = useAccountCategoryControls(category);
  const { data = [], isLoading } = criticalSettingsQuery(
    authConfig,
    controls,
  ).useQuery();

  const table = useTable({
    name: "critical-policy-setting-misconfigurations",
    data,
    columns,
    getRowId: (row) =>
      `${row.id}-${row.policyId}-${row.policyName}-${row.instanceId}`,
  });

  return <VirtualTable table={table} loading={isLoading} height={height} />;
};

const useAccountCategoryControls = (category: ValidControlCategory) => {
  const authConfig = useAuthConfig();
  const { data: account } = useSuspenseQuery(
    queries.iam.getAccount(authConfig),
  );
  const validControlCodes = getCategoryControls(category);
  return account.controls.filter(
    (c) =>
      isValidControl(c.id) &&
      validControlCodes.includes(c.id) &&
      !isDataControl(c.id),
  );
};
